/**
 * Which company this client is signing in to, and which API serves it.
 *
 * Sign-in is two steps: resolve a company code to an API address, then send credentials there. That
 * makes this module's one job security-relevant rather than merely convenient — it decides where a
 * password is posted. Everything here follows from that.
 */

/** What the company step resolves to. Mirrors the API's `TenantEndpointDto`. */
export interface TenantEndpoint {
  /** The canonical code, as the server normalised it — not as the user typed it. */
  code: string;

  /** The trading name, shown so the user can confirm the company before typing a password. */
  name: string;

  /**
   * Where to send everything from here on, or null meaning "keep using this app's own origin".
   *
   * Null is the normal case: a single-deployment platform never sets it, and the relative paths
   * the client already uses keep the dev proxy and the same-origin production build working
   * untouched.
   */
  apiBaseUrl: string | null;
}

/**
 * Whether a resolved URL is one a password may be sent to.
 *
 * The server validates this column on write and again on read, so this is the third check of the
 * same rule — deliberately. The server's copies protect the database from a bad value; this one
 * protects *this browser* from a bad answer, which is a different threat: a DNS hijack or a
 * compromised discovery response reaches the client without ever touching the column.
 *
 * The rules match `TenantApiEndpoint` on the server, and they must stay matched. https only, with
 * loopback exempted so developers can work; no credentials, query or fragment, because
 * `https://rajkot.attirerent.com@evil.example` is a URL pointing at `evil.example` that reads to a
 * human as one pointing somewhere safe.
 */
export function isUsableApiBaseUrl(value: string): boolean {
  // Checked against the raw text, before parsing. `new URL()` resolves `..` away, so a check
  // against `url.pathname` would be inspecting a string the parser had already rewritten and would
  // pass everything. Traversal in a base URL means the value is malformed, whatever it normalises
  // to, and a malformed value here is worth refusing rather than quietly repairing.
  if (value.includes('..')) {
    return false;
  }

  let url: URL;

  try {
    url = new URL(value);
  } catch {
    // Relative, or not a URL at all. Either way there is nothing safe to do with it.
    return false;
  }

  const loopback =
    url.hostname === 'localhost' || url.hostname === '127.0.0.1' || url.hostname === '[::1]';

  if (url.protocol !== 'https:' && !(url.protocol === 'http:' && loopback)) {
    return false;
  }

  if (url.username !== '' || url.password !== '' || url.search !== '' || url.hash !== '') {
    return false;
  }

  return url.hostname !== '';
}

/**
 * The stored form: no trailing slash, because callers build request URLs by concatenation and
 * `https://x.example/` + `/api/auth/login` is `//api/auth/login` — a path most servers treat as
 * different, failing only for whichever tenant happened to be typed with the slash.
 */
function normalizeBaseUrl(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim().replace(/\/+$/, '');

  if (trimmed === '' || !isUsableApiBaseUrl(trimmed)) {
    return null;
  }

  return trimmed;
}

/**
 * Reads an unknown value into a `TenantEndpoint`, or null if it isn't one.
 *
 * Applied to whatever came out of storage as well as to the API's own response. Storage is not a
 * trusted source: it is a string another script on this origin could have written, and the field
 * being validated is the one that names where passwords go.
 */
export function parseTenantEndpoint(value: unknown): TenantEndpoint | null {
  if (value === null || typeof value !== 'object') {
    return null;
  }

  const { code, name, apiBaseUrl } = value as Record<string, unknown>;

  if (typeof code !== 'string' || code.trim() === '' || typeof name !== 'string') {
    return null;
  }

  // An unusable URL degrades to null — this app's own origin — rather than rejecting the whole
  // record. That is the safer failure: sign-in against the wrong deployment fails as bad
  // credentials, whereas honouring a malformed URL is how a password reaches a host nobody vetted.
  return {
    code: code.trim(),
    name,
    apiBaseUrl: normalizeBaseUrl(typeof apiBaseUrl === 'string' ? apiBaseUrl : null),
  };
}
